import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MapPin } from 'lucide-react';

const ProjectsPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'living', name: 'Living Room' },
    { id: 'bedroom', name: 'Bedroom' },
    { id: 'accent', name: 'Accent Walls' },
    { id: 'office', name: 'Home Office' },
  ];

  const projects = [
    {
      id: 1,
      title: 'Modern Living Room Refresh',
      location: 'Manhattan, NY',
      category: 'living',
      image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1200&q=80',
    },
    {
      id: 2,
      title: 'Accent Wall Installation',
      location: 'Brooklyn, NY',
      category: 'accent',
      image: 'https://images.unsplash.com/photo-1615529328331-f8917597711f?auto=format&fit=crop&w=800&q=80',
    },
    {
      id: 3,
      title: 'Luxury Bedroom Styling',
      location: 'Upper East Side, NY',
      category: 'bedroom',
      image: 'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?auto=format&fit=crop&w=800&q=80',
    },
    {
      id: 4,
      title: 'Contemporary Home Office',
      location: 'SoHo, NY',
      category: 'office',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80',
    },
    {
      id: 5,
      title: 'Minimalist Dining Space',
      location: 'Queens, NY',
      category: 'living',
      image: 'https://images.unsplash.com/photo-1617806118233-18e1de247200?auto=format&fit=crop&w=800&q=80',
    },
    {
      id: 6,
      title: 'Cozy Master Bedroom',
      location: 'Tribeca, NY',
      category: 'bedroom',
      image: 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?auto=format&fit=crop&w=1200&q=80',
    },
    {
      id: 7,
      title: 'Bold Geometric Accent Wall',
      location: 'Williamsburg, NY',
      category: 'accent',
      image: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1200&q=80',
    },
    {
      id: 8,
      title: 'Scandinavian Living Room',
      location: 'Chelsea, NY',
      category: 'living',
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1200&q=80',
    },
    {
      id: 9,
      title: 'Executive Home Office',
      location: 'Financial District, NY',
      category: 'office',
      image: 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1200&q=80',
    },
  ];

  const filteredProjects =
    activeFilter === 'all'
      ? projects
      : projects.filter((p) => p.category === activeFilter);

  return (
    <div data-testid="projects-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#FAF9F6]">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#C5A065] mb-4 block">
              Our Portfolio
            </span>
            <h1
              data-testid="projects-page-title"
              className="font-heading text-4xl md:text-5xl text-[#2D2D2D] mb-6"
            >
              Featured Projects
            </h1>
            <p className="text-[#6E6E6E] text-lg leading-relaxed">
              Explore our collection of completed home decoration projects
              across New York. Each space tells a unique story of collaboration
              and transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white border-b border-[#E5E0D8]">
        <div className="container-main">
          <div className="flex flex-wrap gap-4">
            {categories.map((cat) => (
              <button
                key={cat.id}
                data-testid={`filter-${cat.id}`}
                onClick={() => setActiveFilter(cat.id)}
                className={`px-6 py-2 rounded-full text-sm transition-all ${
                  activeFilter === cat.id
                    ? 'bg-[#4A4A4A] text-white'
                    : 'bg-[#FAF9F6] text-[#6E6E6E] hover:bg-[#E5E0D8]'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                data-testid={`project-item-${project.id}`}
                className="project-card aspect-[4/3] rounded-sm overflow-hidden group"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="overlay" />
                <div className="content text-white">
                  <h3 className="font-heading text-xl mb-2">{project.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-white/80">
                    <MapPin className="w-4 h-4" />
                    {project.location}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FAF9F6]">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-[#2D2D2D] mb-6">
            Want Your Home Featured Here?
          </h2>
          <p className="text-[#6E6E6E] max-w-2xl mx-auto mb-8">
            Let's create something beautiful together. Schedule a consultation
            to discuss your project.
          </p>
          <Link to="/contact">
            <Button data-testid="projects-cta-btn" className="btn-primary">
              Start Your Project
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
